import { I18nFormatter } from '../I18n/I18n';
import UserStore from '../../stores/UserStore';
import typeOf from '../../helpers/typeOf';
import { getTranslation } from '../../helpers/translationHelper';
import store from '../../stores/redux';
import Helpers from './Helpers';
import { formatKeys } from './config';

const getCurrencyFromStore = () => {
    const s = store.getState?.() || {};
    return (
        s.user?.publisher?.account?.balance?.currency ||
        s.user?.publisher?.account?.currency ||
        s.user?.currency ||
        'USD'
    );
};

const getTimeZoneFromStore = () => {
    const s = store.getState?.() || {};
    return s.user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
};

const getReportStatic = () => {
    const t = getTranslation('report', {});
    return t || { title: { totalReport: 'Total' }, undefined: '—' };
};

const formatNumber = (v) => {
    return I18nFormatter.int(v);
};

const formatPercent = (v) => {
    return typeOf.isNumeric(v) ? I18nFormatter.percent(v) : v;
};

const formatCurrency = (v, currency, isHtml) => {
    return typeOf.isNumeric(v) ? I18nFormatter.currency(v, currency, isHtml) : v;
};

const formatDate = (value, params) => {
    const type = params.dateType;
    const timeZone = getTimeZoneFromStore();
    switch (type) {
        case 'dateTime':
            return I18nFormatter.dateTime(value, timeZone);
        case 'dateWeek':
            return I18nFormatter.dateWeek(value, timeZone);
        case 'dateMonth':
            return I18nFormatter.dateMonth(value, timeZone);
        case 'dateYear':
            return I18nFormatter.dateYear(value, timeZone);
        case 'dateWeekDay':
            return I18nFormatter.weekDay(value, timeZone);
        default:
            return I18nFormatter.date(value, timeZone);
    }
};

const formatQuarter = (value) => {
    const d = new Date(value);
    const y = d.getFullYear();
    const m = d.getMonth();
    let q = 1;
    if (m >= 4 && m < 6) q = 2;
    if (m >= 6 && m < 9) q = 3;
    if (m >= 9 && m < 12) q = 4;
    return `Q${q} ${y}`;
};

const formatBoolean = (value) => {
    if (typeOf.isNumeric(value)) return value ? `Yes (${value})` : `No (${value})`;
    return value;
};

const formatIds = (id, reportParams) => {
    const type = reportParams.group_by;
    let value = id;
    switch (type) {
        case 'Advertiser':
            value = _getTitle(id, 'advertiser', reportParams);
            break;
        case 'Period':
            value = _getTitle(id, 'period', reportParams);
            break;
        case 'Offer':
            value = _getTitle(id, 'offers', reportParams);
            break;
        case 'Goal':
            value = _getTitle(id, 'goals', reportParams);
            break;
        case 'Link':
            value = _getTitle(id, 'links', reportParams);
            break;
        case 'Country':
        case 'District':
        case 'Area':
        case 'City':
            value = _getTitle(id, 'regions', reportParams);
            break;
        default:
            break;
    }
    return value;
};

const formatString = (v) => {
    return v;
};

const formatArray = (v) => {
    return typeOf.isArray(v) ? v.join(', ') : '';
};

const format = (value = 0, keys, reportParams = {}) => {
    const staticNames = getReportStatic();
    const params = Helpers.getParams(keys);

    if (typeOf.isObject(keys) && keys.isTotals) {
        return staticNames.title?.totalReport || 'Total';
    }

    if (value === undefined || value === null) {
        return staticNames.undefined ?? '—';
    }

    switch (params.format) {
        case formatKeys.number:
            return formatNumber(value);
        case formatKeys.percent:
            return formatPercent(value);
        case formatKeys.currency: {
            const cur = reportParams.currency ?? getCurrencyFromStore();
            return formatCurrency(value, cur, reportParams.isHtml);
        }
        case formatKeys.date:
            return formatDate(value, params);
        case formatKeys.quarter:
            return formatQuarter(value);
        case formatKeys.boolean:
            return formatBoolean(value);
        case formatKeys.ids:
            return formatIds(value, reportParams);
        case formatKeys.string:
            return formatString(value);
        case formatKeys.array:
            return formatArray(value);
        default:
            return !typeOf.isNumeric(value) && !value?.length ? (staticNames.undefined ?? '—') : value;
    }
};

const Formatter = {
    format,
    number: formatNumber,
    percent: formatPercent,
    currency: formatCurrency,
    date: formatDate,
    quarter: formatQuarter,
    boolean: formatBoolean,
    ids: formatIds,
    string: formatString,
    array: formatArray
};

const _getTitle = (id, dataType, reportParams) => {
    const s = store.getState?.() || {};
    const data = s[dataType]?.list || [];
    const staticNames = getReportStatic();
    let title = `(${id}) ${staticNames.undefined ?? '—'}`;

    if (data.length && !reportParams.isChange) {
        const item = data.find((x) => +x.id === +id);
        if (item) title = `(${id}) ${item.title}`;
    } else {
        title = id;
    }
    return title;
};

export default Formatter;
